import { formatDate } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ChargeBodyPut } from "../models/charge-body-put";
import { ChargeMsgResult } from "../models/charge-msg-result";
import { Payment } from "../models/payment";
import { PaymentBodyFilter } from "../models/payment-body-filter";
import { PaymentFilter } from "../models/payment-filter";
import { PaymentResult } from "../models/payment-result";
import { ReconciliationRow } from "../models/reconciliation-row";
import { SingleCharge, SingleChargeResult } from "../models/single-charge";
import { Title, TitlesResponse } from "../models/titulos";
import * as CryptoJS from "crypto-js";

export interface ReconciliationExportParams {
  dt_inicio: string;
  dt_fim: string;
  todas_filiais: boolean;
}

@Injectable({
  providedIn: "root",
})
export class ChargesService {
  private httpOptions = {
    headers: new HttpHeaders({ "Content-Type": "application/json" }),
  };

  constructor(private http: HttpClient) {}

  // Com o filtro por filial desabilitado a consulta abrange todo o grupo de
  // empresas, por isso o cobrador é obrigatório. A regra é aplicada aqui porque
  // todos os caminhos (grade, paginação de cada aba, aba Notificado, Relatório
  // de Recebimentos) passam por este serviço.
  //
  // A condição espelha o parâmetro enviado na URL (`filial !== false`): filtro
  // ainda não definido — o estado do primeiro render — vale filial logada, e
  // portanto não é bloqueio.
  private isFilterAllowed(paymentFilter: PaymentFilter): boolean {
    if (!paymentFilter) return true;
    return paymentFilter.filial !== false || !!paymentFilter.cobrador;
  }

  private emptyPaymentResult(limit: number, page: number): PaymentResult {
    const zero = { value: 0, parcels: 0 };

    return {
      result: [],
      total_docs: 0,
      limit,
      page,
      total: zero,
      credit_card: zero,
      debit_card: zero,
      money: zero,
      pix: zero,
    };
  }

  private buildPaymentBodyFilter(paymentFilter: PaymentFilter): PaymentBodyFilter {
    const bodyFilter: PaymentBodyFilter = {};

    if (!paymentFilter) return bodyFilter;

    if (paymentFilter.dt_recebimento.fromDate && paymentFilter.dt_recebimento.toDate) {
      bodyFilter.dt_recebimentoIni = formatDate(paymentFilter.dt_recebimento.fromDate, "yyyy-MM-dd", "pt-br");
      bodyFilter.dt_recebimentoFim = formatDate(paymentFilter.dt_recebimento.toDate, "yyyy-MM-dd", "pt-br");
    }
    if (paymentFilter.turnoRecebimento) {
      bodyFilter.turnoRecebimento = paymentFilter.turnoRecebimento;
    }
    if (paymentFilter.dt_agenda.fromDate && paymentFilter.dt_agenda.toDate) {
      bodyFilter.dt_agendaIni = formatDate(paymentFilter.dt_agenda.fromDate, "yyyy-MM-dd", "pt-br");
      bodyFilter.dt_agendaFim = formatDate(paymentFilter.dt_agenda.toDate, "yyyy-MM-dd", "pt-br");
    }
    if (paymentFilter.forma_pagamento) {
      bodyFilter.forma_pagamento = paymentFilter.forma_pagamento;
    }
    if (paymentFilter.contrato.length > 0) {
      bodyFilter.contrato = paymentFilter.contrato.map(contrato => contrato.value);
    }
    if (paymentFilter.cobrador) {
      bodyFilter.cobrador = paymentFilter.cobrador;
    }
    if (paymentFilter.tipoPayment && paymentFilter.tipoPayment !== "Todos") {
      bodyFilter.tipoPayment = paymentFilter.tipoPayment;
    }

    return bodyFilter;
  }

  getPayments(
    cnpj: string,
    status: string,
    paymentFilter: PaymentFilter,
    limit: number,
    page: number
  ): Observable<PaymentResult> {
    if (cnpj === "" || status === "" || paymentFilter === null || limit === null || page === null) {
      return of();
    }

    if (!this.isFilterAllowed(paymentFilter)) {
      return of(this.emptyPaymentResult(limit, page));
    }

    const bodyFilter = this.buildPaymentBodyFilter(paymentFilter);

    let url = `${environment.cobranca_novo}/charges/payments/${cnpj}/${status}/${limit}/${page}/list`;

    if (paymentFilter) {
      const filialParam = paymentFilter.filial !== false ? "true" : "false";
      url += `?filial=${filialParam}`;
    }

    return this.http.post<PaymentResult>(url, bodyFilter, this.httpOptions).pipe(catchError(err => of(err)));
  }

  getReconciliationExport(cnpj: string, params: ReconciliationExportParams): Observable<ReconciliationRow[]> {
    if (!cnpj) {
      return of([]);
    }

    return this.http
      .post<ReconciliationRow[]>(
        `${environment.cobranca_novo}/charges/payments/${cnpj}/reconciliation`,
        params,
        this.httpOptions
      )
      .pipe(catchError(() => of([])));
  }

  getPayment(cnpj: string, id: string): Observable<Payment> {
    if (cnpj === "" || id === "") {
      return of();
    }

    return this.http
      .get<PaymentResult>(`${environment.cobranca_novo}/charges/payments/${cnpj}/${id}/detail`)
      .pipe(catchError(err => of(err)));
  }

  private transformToPaymentSchema(single: SingleCharge, titles: Title[], cobrador: string, cnpj: string): any {
    return {
      id_app: CryptoJS.MD5(`${cobrador}-${Date.now()}`).toString(),
      contrato: single.contrato,
      cnpj: cnpj,
      cgc_cliente: single.cgc_cliente,
      cliente: single.nome_cliente,
      mensagem_contrato: "",
      quantidade_parcelas: single.quantidade_parcelas,
      forma_pagamento: single.metodo,
      cobrador: cobrador,
      status: "aprovado",
      dt_cobranca: new Date(single.data_recebimento),
      total: single.valor_pago,
      baixas: titles.map(title => ({
        cliente: single.nome_cliente,
        contrato: title.contrato,
        chave: title.chave,
        valor_titulo: title.valor_titulo,
        data_baixa: new Date(),
        vencimento: title.vencto.split("/").reverse().join(""),
        nsu: single.nsu,
        aut: single.aut,
        pixKeyType: single.pixKeyType,
        pixKey: single.pixKey,
      })),
      origem: "painel",
      lat: 0,
      lng: 0,
    };
  }

  getTitlesCharges(
    cnpj: string,
    limit: number,
    page: number,
    contrato: string,
    cgc_cliente: string
  ): Observable<TitlesResponse> {
    if (cnpj === "") {
      return of({ page: 0, limit: 0, totalDocs: 0, totalPages: 0, result: [] });
    }
    return this.http
      .get<TitlesResponse>(
        `${environment.cobranca_novo}/charges/${cnpj}/${limit}/${page}/list/single/${contrato}/${cgc_cliente}`
      )
      .pipe(
        catchError(() => of({ page: 0, limit: 0, totalDocs: 0, totalPages: 0, result: [] })),
        map(response => ({
          page: response.page,
          limit: response.limit,
          totalDocs: response.totalDocs,
          totalPages: response.totalPages,
          result: response.result,
        }))
      );
  }
  //  getNotifiedCharges(cnpj: string, limit: number, page: number) : Observable<NotifiedChargesResult>{
  //     if (cnpj === "") {
  //         return of();
  //     }
  // return this.http.get<{
  //     page: number;
  //     limit: number;
  //     total_docs: number;
  //     result: NotifiedCharge[];
  // }>(`${environment.cobranca_novo}/integration/charges/notified/${cnpj}/${limit}/${page}`)
  //       .pipe(catchError(err => of(err)));
  // }
  async saveSingleChargeLink(cobrador: string, single: SingleCharge, titles: Title[], cnpj: string) {
    const paymentPayload = this.transformToPaymentSchema(single, titles, cobrador, cnpj);
    await this.http
      .post(`${environment.cobranca_novo}/charges/payments/${cobrador}/save?issingle=${single._id}`, paymentPayload)
      .toPromise();
  }

  getSingleCharges(
    cnpj: string,
    limit: number,
    page: number,
    paymentFilter?: PaymentFilter
  ): Observable<SingleChargeResult> {
    if (cnpj === "") {
      return of();
    }

    if (!this.isFilterAllowed(paymentFilter)) {
      return of({ result: [], total_docs: 0, limit, page } as SingleChargeResult);
    }

    let url = `${environment.cobranca_novo}/singlecharges/${cnpj}/${limit}/${page}/list`;
    if (paymentFilter) {
      const filialParam = paymentFilter.filial !== false ? "true" : "false";
      url += `?filial=${filialParam}`;
    }
    return this.http
      .get<{
        page: number;
        limit: number;
        total_docs: number;
        single_charges: SingleCharge[];
      }>(url)
      .pipe(catchError(err => of(err)));
  }

  updateCharges(cnpj: string, type: string, status: string, chargeBodyPut: ChargeBodyPut): Observable<ChargeMsgResult> {
    if (cnpj === "" || type === "" || status === "" || chargeBodyPut === null) {
      return of();
    }
    if (chargeBodyPut.cod_motivo_devolucao && chargeBodyPut.motivo_devolucao) {
      delete chargeBodyPut.motivo_devolucao;
      delete chargeBodyPut.cod_motivo_devolucao;
    }
    return this.http
      .put<ChargeMsgResult>(
        `${environment.cobranca_novo}/charges/${type}/${cnpj}/${status}/update`,
        chargeBodyPut,
        this.httpOptions
      )
      .pipe(catchError(err => of(err)));
  }

  requestChargeback(payload: any): Observable<any> {
    return this.http
      .post<any>(`${environment.cobranca_novo}/chargebacks/request`, payload, this.httpOptions)
      .pipe(catchError(err => of(err)));
  }

  getPixStatus(
    cnpj: string,
    cgcCliente: string = "",
    txid: string = "",
    status: string = "REGISTRADO",
    dtInicial?: string,
    dtFinal?: string,
    turno?: string,
    filial?: boolean,
    codigoExterno?: string
  ): Observable<any> {
    if (!cnpj) {
      return of([]);
    }

    let url = `${environment.cobranca_novo}/charges/${cnpj}/pix/status`;

    const params = new URLSearchParams();
    params.append("status", "REGISTRADO");
    if (cgcCliente && cgcCliente.trim() !== "") params.append("cgc_cliente", cgcCliente);
    if (txid && txid.trim() !== "") params.append("txid", txid);
    if (dtInicial) params.append("dt_inicial", dtInicial);
    if (dtFinal) params.append("dt_final", dtFinal);
    if (turno) params.append("turno", turno);
    if (filial !== undefined) params.append("filial", filial ? "true" : "false");
    if (codigoExterno && codigoExterno.trim() !== "") params.append("codigo_externo", codigoExterno);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    return this.http.get<any>(url).pipe(
      catchError(err => {
        console.error("Erro ao buscar PIX não pagos:", err);
        return of([]);
      })
    );
  }
}
